import React, { useEffect, useState } from "react";
import { Modal, Button, Form, Alert } from "react-bootstrap";
import { clientVariableLineService } from "../../services/clientVariableLine.service";

// Values are edited as one per line and parsed into {value, displayOrder} pairs on save --
// simpler than a drag-reorder row editor, and line order IS the display order.
function valuesToText(values) {
  return [...(values || [])]
    .sort((a, b) => a.displayOrder - b.displayOrder)
    .map((v) => v.value)
    .join("\n");
}

function textToValues(text) {
  return text
    .split("\n")
    .map((line) => line.trim())
    .filter((line) => line.length > 0)
    .map((value, index) => ({ value, displayOrder: index }));
}

// Used both the first time (seeded with the line's current free-text value, converting it into
// a picklist) and for later edits (add/remove values, or clear everything to revert the line
// back to plain free text) -- same form either way. Saves directly via the service rather than
// through Redux, since this is a short-lived, self-contained operation; the caller refetches
// (getPicklistValues) via onSaved to pick up the result.
function EditPicklistValuesModal({ show, onHide, variableLineId, clientId, clientGuid, initialValues, onSaved }) {
  const [valuesText, setValuesText] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (show) {
      setValuesText(valuesToText(initialValues));
      setError(false);
    }
    // eslint-disable-next-line
  }, [show]);

  const save = (values) => {
    setSaving(true);
    setError(false);

    clientVariableLineService
      .setPicklistValues({ variableLineId, values, clientId, clientGuid })
      .then(() => {
        setSaving(false);
        onSaved();
        onHide();
      })
      .catch(() => {
        setSaving(false);
        setError(true);
      });
  };

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title>Edit Picklist</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && <Alert variant="danger">Failed to save. Please try again.</Alert>}
        <Form.Group>
          <Form.Label>Values</Form.Label>
          <Form.Control
            as="textarea"
            rows={8}
            value={valuesText}
            onChange={(e) => setValuesText(e.target.value)}
            placeholder={"One value per line, e.g.\nYes\nNo\nNone"}
          />
          <Form.Text className="text-muted">
            One value per line. Order here is the suggestion order shown when entering this line's value.
          </Form.Text>
        </Form.Group>
      </Modal.Body>
      <Modal.Footer className="d-flex justify-content-between">
        <Button
          variant="outline-danger"
          onClick={() => save([])}
          disabled={saving}
        >
          Remove Picklist
        </Button>
        <div>
          <Button variant="outline-secondary" onClick={onHide} className="me-2" disabled={saving}>
            Cancel
          </Button>
          <Button variant="primary" onClick={() => save(textToValues(valuesText))} disabled={saving}>
            {saving ? "Saving..." : "Save"}
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
}

export default EditPicklistValuesModal;
